// pages/AmPassport.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dryrun } from "@permaweb/aoconnect";
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';

// Import the Profile component and utilities
import Profile from '../components/Profile';
import PortalLoadingScreen from '../components/features/PortalLoadingScreen';
import { AO_PROCESS_ID } from '../utils/constants';
import { safeParseJson, retry } from '../utils/helpers';

// Custom hook for wallet detection (same as in AmVerify)
const useArConnectDetection = () => {
  const [walletDetected, setDetected] = useState(false);

  useEffect(() => {
    let interval;
    let timeout;

    const flagDetected = () => {
      if (window.arweaveWallet && typeof window.arweaveWallet.connect === "function") {
        setDetected(true);
        clearInterval(interval);
        clearTimeout(timeout);
      }
    };

    flagDetected();

    if (!walletDetected) {
      interval = setInterval(flagDetected, 500);
      timeout = setTimeout(() => {
        clearInterval(interval);
      }, 10000);
    }

    window.addEventListener("arweaveWalletLoaded", flagDetected);

    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
      window.removeEventListener("arweaveWalletLoaded", flagDetected);
    };
  }, []);

  return walletDetected;
};

function AmPassport() {
  const navigate = useNavigate();
  const walletDetected = useArConnectDetection();
  const [walletConnected, setWalletConnected] = useState(false);
  const [address, setAddress] = useState("");
  const [publicKey, setPublicKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hasAccount, setHasAccount] = useState(null);
  const [discordId, setDiscordId] = useState("");
  const [checkAttempts, setCheckAttempts] = useState(0);

  // Check wallet connection on mount
  useEffect(() => {
    const checkWalletConnection = async () => {
      if (!window.arweaveWallet) {
        setLoading(false);
        return;
      }
      
      try {
        const addr = await window.arweaveWallet.getActiveAddress();
        if (addr) {
          let pk = "";
          try {
            pk = await window.arweaveWallet.getActivePublicKey();
          } catch (e) {
            console.warn("Could not get public key:", e);
          }
          
          setAddress(addr);
          setPublicKey(pk);
          setWalletConnected(true);
          
          // Check if user has an account
          await checkUserAccount(addr);
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.log("No active wallet connection");
        setLoading(false);
      }
    };

    if (walletDetected) {
      checkWalletConnection();
    } else {
      // Set timeout to stop loading if wallet not detected
      const timeout = setTimeout(() => {
        setLoading(false);
      }, 3000);
      
      return () => clearTimeout(timeout);
    }
  }, [walletDetected]);

  const checkUserAccount = useCallback(async (walletAddress, isRetry = false) => {
    if (!isRetry) {
      setLoading(true);
    }
    setError("");
    
    try {
      const nonce = Math.random().toString(36).slice(2);
      
      // Use retry helper for better reliability
      const res = await retry(() => dryrun({
        process: AO_PROCESS_ID,
        tags: [
          { name: "Action", value: "Get-User-By-Wallet" },
          { name: "Wallet-Address", value: walletAddress },
          { name: "Nonce", value: nonce },
        ],
      }), 3, 2000); // 3 retries with 2 second delay

      const data = safeParseJson(res?.Messages?.[0]?.Data);
      if (data?.Success && data?.userId) {
        setHasAccount(true);
        setDiscordId(data.userId);
        setCheckAttempts(0);
      } else {
        // If first attempt, wait and try once more
        if (checkAttempts < 1 && !isRetry) {
          setCheckAttempts(prev => prev + 1);
          setTimeout(() => {
            checkUserAccount(walletAddress, true);
          }, 3000);
          return; // Don't set hasAccount false yet
        }
        setHasAccount(false);
        setCheckAttempts(0);
      }
    } catch (err) {
      console.error("Error checking account:", err);
      // On error, if first attempt, try once more
      if (checkAttempts < 1 && !isRetry) {
        setCheckAttempts(prev => prev + 1);
        setTimeout(() => {
          checkUserAccount(walletAddress, true);
        }, 3000);
        return;
      }
      setHasAccount(false);
      setError("Failed to check account status. Please try again.");
      setCheckAttempts(0);
    } finally {
      if (!isRetry || checkAttempts >= 1) {
        setLoading(false);
      }
    }
  }, [checkAttempts]);

  const connectWallet = async () => {
    if (!walletDetected || !window.arweaveWallet) {
      setError("Wallet not detected. Please install ArConnect.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const permissions = [
        "ACCESS_ADDRESS",
        "SIGNATURE",
        "ACCESS_PUBLIC_KEY",
        "ACCESS_TOKENS",
        "ACCESS_ALL_ADDRESSES",
        "SIGN_TRANSACTION",
        "DISPATCH",
      ];

      await window.arweaveWallet.connect(permissions, {
        name: "amPassport",
        logo: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
      });

      const addr = await window.arweaveWallet.getActiveAddress();
      if (!addr) throw new Error("Failed to get wallet address");

      let pk = "";
      try {
        pk = await window.arweaveWallet.getActivePublicKey();
      } catch (e) {
        console.warn("Could not get public key:", e);
      }

      setAddress(addr);
      setPublicKey(pk);
      setWalletConnected(true);
      
      await checkUserAccount(addr);
    } catch (err) {
      setError(`Failed to connect: ${err.message}`);
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (address) {
      setCheckAttempts(0);
      checkUserAccount(address);
    }
  };

  // Show portal loading screen during initial load
  if (loading && walletConnected) {
    return <PortalLoadingScreen />;
  }

  // If wallet not connected, show connect screen
  if (!walletConnected) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-950 via-black to-green-900 text-white flex items-center justify-center">
        <div className="fixed inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%2300ff00%22 fill-opacity=%220.03%22%3E%3Cpath d=%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 text-center max-w-md"
        >
          <h1 className="text-3xl font-bold mb-4">Connect Wallet to Continue</h1>
          <p className="text-white/60 mb-8">
            Connect your Arweave wallet to access your amPassport profile
          </p>
          
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={connectWallet}
            disabled={!walletDetected || loading}
            className="px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-gray-600 disabled:to-gray-700 rounded-lg font-semibold transition-all flex items-center space-x-2 mx-auto"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Connecting...</span>
              </>
            ) : walletDetected ? (
              <span>Connect Wallet</span>
            ) : (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Detecting Wallet...</span>
              </>
            )}
          </motion.button>
        </motion.div>
      </div>
    );
  }

  // If no account found
  if (hasAccount === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-950 via-black to-green-900 text-white flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 text-center max-w-md"
        >
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-4">No amPassport Found</h1>
          <p className="text-white/60 mb-8">
            This wallet doesn't have an amPassport account yet. 
            Please start the verification process from Discord first.
          </p>
          
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}
          
          <div className="flex gap-3 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleRefresh}
              className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-lg font-semibold transition-all flex items-center gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              Try Again
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/amverify')}
              className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 rounded-lg font-semibold transition-all"
            >
              Go to Verification
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }
  // If account exists, show profile
  return (
    <Profile
      address={address}
      walletConnected={walletConnected}
      linkGithub={() => window.location.href = `/amverify?linkGithub=true`}
      linkX={() => window.location.href = `/amverify?linkX=true`}
      linkDiscord={() => window.location.href = `/amverify?linkDiscord=true`}
      onDiscordIdFound={(id) => setDiscordId(id)}
      onFetchUserError={(msg) => setError(msg)}
    />
  );
}

export default AmPassport;